import { Decoration, DecorationSet } from 'prosemirror-view'
import { Plugin, PluginKey } from 'prosemirror-state'
import { autoUpdate } from '@floating-ui/dom'
import { render, unmountComponentAtNode } from 'react-dom'

import Styles from './BorderFormatBarPlugin.module.scss'
import { BorderFormatBar } from './components/BorderFormatBar/BorderFormatBar'
import { BorderSelection } from 'common/Prose/utils/table/BorderSelection'
import { determineDelimitingBorders } from './utils/determineDelimitingBorders'
import { determineDelimitingOffsets } from './utils/determineDelimitingOffsets'
import { tablePluginKey as TableRightClickMenuPluginKey } from 'common/Prose/Plugins/TableRightClickMenu/TableRightClickMenu'

interface PluginState {
  decoration: Decoration | null
  wrapper: HTMLElement | null
}

let autoUpdateCleanup: ReturnType<typeof autoUpdate> | null = null

const VIEWPORT_MARGIN = 16

const BorderFormatBarPluginKey = new PluginKey('BorderFormatBar')
export const BorderFormatBarPlugin = new Plugin({
  key: BorderFormatBarPluginKey,
  props: {
    decorations: (editorState) => {
      const { decoration }: PluginState = BorderFormatBarPluginKey.getState(editorState)
      if (!decoration) return

      return DecorationSet.create(editorState.doc, [decoration])
    },
  },
  state: {
    apply: (tr, pluginState, oldEditorState, newEditorState): PluginState => {
      // NOTE: if the format bar should be hidden
      if (
        TableRightClickMenuPluginKey.getState(newEditorState)?.active ||
        !(newEditorState.selection instanceof BorderSelection)
      ) {
        const { wrapper } = pluginState
        if (wrapper) unmountComponentAtNode(wrapper)
        if (autoUpdateCleanup) {
          autoUpdateCleanup()
          autoUpdateCleanup = null
        }

        return { decoration: null, wrapper: null }
      }

      // NOTE: if the doc didn't change and the format bar didn't become active
      // either
      if (oldEditorState.selection.eq(newEditorState.selection) && !tr.docChanged) return pluginState

      // NOTE: in all other cases, construct a new decoration so react has a
      // chance to re-render (or initially render)
      const { wrapper: existingWrapper }: PluginState = BorderFormatBarPluginKey.getState(oldEditorState)
      const wrapper = existingWrapper ?? document.createElement('div')
      if (!existingWrapper) {
        wrapper.classList.add(Styles.wrapper)
        // NOTE: Not sure why this is necessary, but without it the bar
        // disappears, because prosemirror detects dom mutations?
        wrapper.addEventListener('mousedown', (e) => e.preventDefault())
      }

      return {
        decoration: Decoration.widget(
          newEditorState.selection.$tablePos.pos,
          (view) => {
            // NOTE: wait for the table to update its dimensions
            setTimeout(() => {
              if (!(view.state.selection instanceof BorderSelection)) return

              const borderSelection = view.state.selection
              const tableElement = view.domAtPos(borderSelection.$tablePos.pos + 1)?.node?.parentElement as
                | HTMLElement
                | undefined
              if (!tableElement) return

              const tableScrollContainer = tableElement.parentElement
              if (!tableScrollContainer) return

              const tableWrapper = tableScrollContainer.parentElement
              if (!tableWrapper) return

              autoUpdateCleanup = autoUpdate(tableElement, wrapper, async () => {
                if (!(view.state.selection instanceof BorderSelection)) return
                const borderSelection = view.state.selection

                const delimitingBorders = determineDelimitingBorders(borderSelection)
                const { left, right, top } = determineDelimitingOffsets(delimitingBorders, borderSelection, view)
                if (left === undefined || right === undefined || top === undefined) return

                const viewportWidth = document.body.getBoundingClientRect().width

                // NOTE: clamp to right (Math.min) and left (Math.max) table
                // wrapper edges (with a margin of half the format bar width)
                // so the format bar doesn't show up way outside of the
                // editor
                const targetLeft = Math.min(
                  Math.max(
                    (left + right) / 2 - tableScrollContainer.scrollLeft,
                    tableElement.offsetLeft - wrapper.offsetWidth / 2,
                    -tableWrapper.getBoundingClientRect().left + wrapper.offsetWidth / 2 + VIEWPORT_MARGIN,
                  ),
                  tableWrapper.offsetLeft + tableWrapper.offsetWidth,
                  viewportWidth -
                    parseInt(Styles.rightRailWidth) -
                    tableWrapper.getBoundingClientRect().left -
                    wrapper.offsetWidth / 2 -
                    VIEWPORT_MARGIN,
                )

                const targetRight = right - tableScrollContainer.scrollLeft + tableElement.offsetLeft

                // NOTE: If the border selection isn't visible anymore -> hide
                // the format bar
                const hideFormatBar =
                  left - tableScrollContainer.scrollLeft > tableWrapper.offsetLeft + tableWrapper.offsetWidth ||
                  targetRight < tableWrapper.offsetLeft

                if (hideFormatBar) wrapper.style.opacity = '0'
                else wrapper.style.opacity = '1'

                wrapper.style.setProperty('--border-format-bar-left', `${targetLeft}px`)

                wrapper.style.setProperty('--border-format-bar-top', `${tableWrapper.offsetTop + top}px`)
              })
            })

            render(<BorderFormatBar view={view} />, wrapper)

            return wrapper
          },
          { stopEvent: () => true },
        ),
        wrapper,
      }
    },
    init: (): PluginState => ({ decoration: null, wrapper: null }),
  },
})
